// Page Cercle de Confiance - Super Admin
// Gestion des invitations "Super Confiance" et "Confiance"

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, ShieldCheck, Users, UserPlus, Mail, 
  Trash2, Eye, Clock, CheckCircle, X, ChevronDown,
  ChevronRight, AlertCircle, Search, RefreshCw, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Couleurs
const C = {
  navy: '#1E3A5F',
  orange: '#F97316',
  green: '#22c55e',
  red: '#ef4444',
  cream: '#FFFBF7',
};

export default function CercleConfiancePage() {
  const [activeTab, setActiveTab] = useState('super_confiance');
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInvitesModal, setShowInvitesModal] = useState(null); // email du super confiance
  const [subInvites, setSubInvites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Formulaire
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    note: '',
    trust_level: 'super_confiance'
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  // Charger les données
  useEffect(() => {
    fetchInvites();
    fetchStats();
  }, [activeTab]);

  const fetchInvites = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/trust-circle/invites?trust_level=${activeTab}`);
      const data = await res.json();
      setInvites(data.invites || []);
    } catch (error) {
      console.error('Erreur chargement invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/trust-circle/stats`);
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const fetchSubInvites = async (email) => {
    try {
      const res = await fetch(`${API_URL}/api/trust-circle/invites/by/${encodeURIComponent(email)}`);
      const data = await res.json();
      setSubInvites(data.invites || []);
      setShowInvitesModal(email);
    } catch (error) {
      console.error('Erreur chargement sous-invitations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!formData.email || !formData.name) {
      setFormError('Email et nom sont obligatoires');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/trust-circle/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          trust_level: activeTab
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setFormError(data.detail || 'Erreur lors de l\'invitation');
        return;
      }

      // Afficher le statut de l'email
      const emailStatus = data.email_sent 
        ? `Invitation envoyée à ${formData.email} (email envoyé ✓)` 
        : `Invitation créée pour ${formData.email} (email en attente de configuration domaine)`;
      
      setFormSuccess(emailStatus);
      setFormData({ email: '', name: '', note: '', trust_level: activeTab });
      fetchInvites();
      fetchStats();
      
      setTimeout(() => {
        setShowAddModal(false);
        setFormSuccess('');
      }, 2500);
    } catch (error) {
      setFormError('Erreur réseau');
    }
  };

  const handleDelete = async (inviteId) => {
    if (!window.confirm('Supprimer cette invitation ?')) return;

    try {
      const res = await fetch(`${API_URL}/api/trust-circle/invite/${inviteId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        fetchInvites();
        fetchStats();
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const handleResendEmail = async (inviteId, email) => {
    try {
      const res = await fetch(`${API_URL}/api/trust-circle/resend/${inviteId}`, {
        method: 'POST'
      });

      const data = await res.json();

      if (res.ok && data.success) {
        alert(`Email renvoyé à ${email} ✓`);
      } else {
        alert(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Erreur renvoi email:', error);
      alert('Erreur réseau');
    }
  };

  // Filtrer par recherche
  const filteredInvites = invites.filter(inv => 
    inv.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ 
          fontSize: 28, 
          fontWeight: 800, 
          color: C.navy,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 8
        }}>
          <Shield size={32} style={{ color: C.orange }} />
          Cercle de Confiance
        </h1>
        <p style={{ color: '#64748b', fontSize: 15 }}>
          Gérer les personnes de confiance qui n'ont pas besoin de certification
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 32
        }}>
          <StatCard 
            icon={<ShieldCheck size={24} />}
            label="Super Confiance"
            value={stats.super_confiance}
            color="#8b5cf6"
          />
          <StatCard 
            icon={<Shield size={24} />}
            label="Confiance"
            value={stats.confiance}
            color={C.orange}
          />
          <StatCard 
            icon={<Clock size={24} />}
            label="En attente"
            value={stats.pending}
            color="#f59e0b"
          />
          <StatCard 
            icon={<CheckCircle size={24} />}
            label="Inscrits"
            value={stats.registered}
            color={C.green}
          />
        </div>
      )}

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: 8, 
        marginBottom: 24,
        borderBottom: '2px solid #e2e8f0',
        paddingBottom: 12
      }}>
        <TabButton 
          active={activeTab === 'super_confiance'}
          onClick={() => setActiveTab('super_confiance')}
          icon={<ShieldCheck size={18} />}
          label="Super Confiance"
          count={stats?.super_confiance}
          color="#8b5cf6"
        />
        <TabButton 
          active={activeTab === 'confiance'}
          onClick={() => setActiveTab('confiance')}
          icon={<Shield size={18} />}
          label="Confiance"
          count={stats?.confiance}
          color={C.orange}
        />
      </div>

      {/* Actions Bar */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        flexWrap: 'wrap',
        gap: 12
      }}>
        {/* Search */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 8,
          background: '#f1f5f9',
          padding: '8px 16px',
          borderRadius: 12,
          flex: '1 1 300px',
          maxWidth: 400
        }}>
          <Search size={18} style={{ color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              border: 'none',
              background: 'transparent',
              outline: 'none',
              flex: 1,
              fontSize: 14
            }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => { fetchInvites(); fetchStats(); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 16px',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              background: 'white',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              color: '#64748b'
            }}
          >
            <RefreshCw size={16} />
            Actualiser
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              background: activeTab === 'super_confiance' ? '#8b5cf6' : C.orange,
              color: 'white',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            <UserPlus size={18} />
            Inviter
          </button>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          Chargement...
        </div>
      ) : filteredInvites.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 60, 
          background: '#f8fafc',
          borderRadius: 16,
          color: '#94a3b8'
        }}>
          <Users size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
          <p>Aucune invitation {activeTab === 'super_confiance' ? 'Super Confiance' : 'Confiance'}</p>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              background: C.navy,
              color: 'white',
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            Ajouter la première
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredInvites.map((invite) => (
            <InviteCard 
              key={invite.id}
              invite={invite}
              onDelete={() => handleDelete(invite.id)}
              onResendEmail={() => handleResendEmail(invite.id, invite.email)}
              onViewInvites={() => fetchSubInvites(invite.email)}
              isSuperConfiance={activeTab === 'super_confiance'}
            />
          ))}
        </div>
      )}

      {/* Modal Ajouter */}
      <AnimatePresence>
        {showAddModal && (
          <Modal onClose={() => setShowAddModal(false)}>
            <h2 style={{ 
              fontSize: 20, 
              fontWeight: 700, 
              color: C.navy,
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              {activeTab === 'super_confiance' ? (
                <><ShieldCheck size={24} style={{ color: '#8b5cf6' }} /> Inviter Super Confiance</>
              ) : (
                <><Shield size={24} style={{ color: C.orange }} /> Inviter Confiance</>
              )}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemple.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    fontSize: 15,
                    outline: 'none'
                  }}
                  required
                />
                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                  La personne devra s'inscrire avec cet email exact
                </p>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Nom *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Prénom Nom"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    fontSize: 15,
                    outline: 'none'
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Note (optionnel)
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Note personnelle..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 10,
                    border: '1px solid #e2e8f0',
                    fontSize: 15,
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Info box */}
              <div style={{
                background: activeTab === 'super_confiance' ? 'rgba(139,92,246,0.1)' : 'rgba(249,115,22,0.1)',
                padding: 16,
                borderRadius: 12,
                marginBottom: 20,
                fontSize: 13,
                color: C.navy
              }}>
                {activeTab === 'super_confiance' ? (
                  <>
                    <strong>Super Confiance :</strong> Cette personne pourra inviter d'autres personnes qui n'auront pas besoin de certification.
                  </>
                ) : (
                  <>
                    <strong>Confiance :</strong> Cette personne n'aura pas besoin de certification mais ne pourra pas inviter d'autres personnes avec ce privilège.
                  </>
                )}
              </div>

              {formError && (
                <div style={{
                  background: 'rgba(239,68,68,0.1)',
                  color: C.red,
                  padding: 12,
                  borderRadius: 10,
                  marginBottom: 16,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <AlertCircle size={18} />
                  {formError}
                </div>
              )}

              {formSuccess && (
                <div style={{
                  background: 'rgba(34,197,94,0.1)',
                  color: C.green,
                  padding: 12,
                  borderRadius: 10,
                  marginBottom: 16,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <CheckCircle size={18} />
                  {formSuccess}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    flex: 1,
                    padding: '14px',
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#64748b'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '14px',
                    borderRadius: 12,
                    border: 'none',
                    background: activeTab === 'super_confiance' ? '#8b5cf6' : C.orange,
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: 15,
                    fontWeight: 600
                  }}
                >
                  Envoyer l'invitation
                </button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>

      {/* Modal Voir les invités d'un Super Confiance */}
      <AnimatePresence>
        {showInvitesModal && (
          <Modal onClose={() => setShowInvitesModal(null)}>
            <h2 style={{ 
              fontSize: 20, 
              fontWeight: 700, 
              color: C.navy,
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              <Users size={24} style={{ color: '#8b5cf6' }} />
              Personnes invitées par {showInvitesModal}
            </h2>

            {subInvites.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40, 
                color: '#94a3b8'
              }}>
                <Users size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
                <p>Cette personne n'a encore invité personne</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {subInvites.map((inv) => (
                  <div
                    key={inv.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: '#f8fafc',
                      borderRadius: 10
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, color: C.navy }}>{inv.name}</div>
                      <div style={{ fontSize: 13, color: '#64748b' }}>{inv.email}</div>
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      background: inv.status === 'registered' ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)',
                      color: inv.status === 'registered' ? C.green : '#f59e0b'
                    }}>
                      {inv.status === 'registered' ? 'Inscrit' : 'En attente'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowInvitesModal(null)}
              style={{
                width: '100%',
                marginTop: 20,
                padding: '14px',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                background: 'white',
                cursor: 'pointer',
                fontSize: 15,
                fontWeight: 600,
                color: '#64748b'
              }}
            >
              Fermer
            </button>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANTS
// ═══════════════════════════════════════════════════════════════════════════════

function StatCard({ icon, label, value, color }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 16,
      padding: 20,
      border: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      gap: 16
    }}>
      <div style={{
        width: 48,
        height: 48,
        borderRadius: 12,
        background: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 800, color: '#1E3A5F' }}>{value}</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>{label}</div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, count, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 20px',
        borderRadius: 10,
        border: 'none',
        background: active ? color : 'transparent',
        color: active ? 'white' : '#64748b',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 600,
        transition: 'all 0.2s'
      }}
    >
      {icon}
      {label}
      {count !== undefined && (
        <span style={{
          background: active ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
          padding: '2px 8px',
          borderRadius: 10,
          fontSize: 12
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

function InviteCard({ invite, onDelete, onResendEmail, onViewInvites, isSuperConfiance }) {
  const isRegistered = invite.status === 'registered';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'white',
        borderRadius: 16,
        padding: 20,
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {/* Avatar */}
        <div style={{
          width: 50,
          height: 50,
          borderRadius: '50%',
          background: isSuperConfiance ? 'rgba(139,92,246,0.1)' : 'rgba(249,115,22,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: isSuperConfiance ? '#8b5cf6' : '#F97316',
          fontWeight: 700,
          fontSize: 18
        }}>
          {invite.name.charAt(0).toUpperCase()}
        </div>
        
        {/* Info */}
        <div>
          <div style={{ fontWeight: 700, color: '#1E3A5F', fontSize: 16 }}>
            {invite.name}
          </div>
          <div style={{ fontSize: 14, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Mail size={14} />
            {invite.email}
          </div>
          {invite.note && (
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
              "{invite.note}"
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {/* Status */}
        <span style={{
          padding: '6px 12px',
          borderRadius: 20,
          fontSize: 13,
          fontWeight: 600,
          background: isRegistered ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)',
          color: isRegistered ? '#22c55e' : '#f59e0b',
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}>
          {isRegistered ? <CheckCircle size={14} /> : <Clock size={14} />}
          {isRegistered ? 'Inscrit' : 'En attente'}
        </span>

        {/* Nombre d'invités (pour Super Confiance inscrit) */}
        {isSuperConfiance && isRegistered && (
          <button
            onClick={onViewInvites}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 20,
              border: '1px solid #e2e8f0',
              background: 'white',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              color: '#64748b'
            }}
          >
            <Users size={14} />
            {invite.invites_count || 0} invité(s)
            <ChevronRight size={14} />
          </button>
        )}

        {/* Resend Email (seulement si en attente) */}
        {!isRegistered && (
          <button
            onClick={onResendEmail}
            title="Renvoyer l'email d'invitation"
            style={{
              padding: 8,
              borderRadius: 10,
              border: 'none',
              background: 'rgba(30,58,95,0.1)',
              color: '#1E3A5F',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Send size={18} />
          </button>
        )}

        {/* Delete (seulement si en attente) */}
        {!isRegistered && (
          <button
            onClick={onDelete}
            title="Supprimer l'invitation"
            style={{
              padding: 8,
              borderRadius: 10,
              border: 'none',
              background: 'rgba(239,68,68,0.1)',
              color: '#ef4444',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Trash2 size={18} />
          </button>
        )}
      </div>
    </motion.div>
  );
}

function Modal({ children, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 20
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 20,
          padding: 28,
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            padding: 8,
            borderRadius: '50%',
            border: 'none',
            background: '#f1f5f9',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X size={18} style={{ color: '#64748b' }} />
        </button>
        {children}
      </motion.div>
    </motion.div>
  );
}
